import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@/lib/components/Button';
import FormField from '@/lib/components/FormField';
import { cleanObjectOnValues } from '@/lib/utils/objectUtils';
import { useFormSave } from '@/lib/hooks/useFormSave';

import styles from '@/lib/components/PspComposer/PspComposer.module.scss';

/**
 * This component is one of the multiple available payment method in this project.
 * It represents a form used for SouthAfrica the bank account number,
 * and some information related to SouthAfrica. It is only used to collect refund request data.
 */
const SouthAfricanForm = React.forwardRef(
    (
        {
            pspId,
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton = false,
            labelPayButton = null,  // Fallback on 'pay' or 'ask refund' label.
            onPaymentResult,
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const formRef = React.useRef(null);
        const [formData, setFormData] = React.useState({
            address: '',
            bankAccountHolderName: '',
            bankAccountNumber: '',
            bankBranchCode: '',
            country: '',
            email: '',
            phoneNumber: '',
        });
        const [errors, setErrors] = React.useState({});

        const handleSuccess = React.useCallback(
            (response) => {
                onPaymentResult({ success: true, data: response });
            },
            [onPaymentResult],
        );

        const handleError = React.useCallback(
            (response) => {
                if (response?.data) {
                    const fieldErrors = {};
                    Object
                        .keys(response.data)
                        .forEach((key) => {
                            fieldErrors[key] = response.data[key] && response.data[key].map((error) => t([
                                `south_african_form_${key}_error_${error.code}`,
                                `error_${error.code}`,
                                'error_form',
                            ]));
                        });
                    setErrors(fieldErrors);
                }
                onPaymentResult({
                    success: false,
                    data: response,
                });
            },
            [onPaymentResult, t],
        );

        const url = lyriaUrl + '/south-african-form/save';
        const saveForm = useFormSave(url, token, paymentRequestId, pspId, handleSuccess, handleError);

        const submitForm = React.useCallback(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                const rawData = {
                    address: formData.address,
                    bank_account_holder_name: formData.bankAccountHolderName,
                    bank_account_number: formData.bankAccountNumber,
                    bank_branch_code: formData.bankBranchCode,
                    country: formData.country,
                    email: formData.email,
                    phone_number: formData.phoneNumber,
                };
                const data  = cleanObjectOnValues(rawData, ['', null]);
                saveForm(data);
            },
            [formData, saveForm],
        );

        const onChangeFormField = React.useCallback(
            (fieldName) => (event, newValue) => {
                setFormData((prevState) => ({
                    ...prevState,
                    [fieldName]: newValue,
                }));

            },
            [],
        );

        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    return formRef.current.click();
                },
                canClickOnPay: Object.values(formData).every((value) => !!value),
            }),
            [formData],
        );

        const requiredFields = [
            'address',
            'bankAccountHolderName',
            'bankAccountNumber',
            'bankBranchCode',
            'country',
            'email',
        ];
        const hasRequiredFieldNotFilled = Object
            .values(
                Object.fromEntries(
                    Object
                        .entries(formData)
                        .filter(([k, _]) => requiredFields.includes(k)),
                ),
            )
            .some((value) => !value);

        return (
            <div id="wz-lyriapay__south-african-form" className={styles.common_psp}>
                <form noValidate onSubmit={submitForm}>
                    <div className={styles.common_psp_form}>
                        <FormField
                            name="email"
                            value={formData?.email}
                            onChange={onChangeFormField('email')}
                            label={t('south_african_form_email')}
                            errors={errors.email}
                            required
                        />
                        <FormField
                            name="bank_account_holder_name"
                            value={formData?.bankAccountHolderName}
                            onChange={onChangeFormField('bankAccountHolderName')}
                            label={t('south_african_form_bank_account_holder_name')}
                            errors={errors.bank_account_holder_name}
                            required
                        />
                        <FormField
                            name="bank_account_number"
                            value={formData?.bankAccountNumber}
                            onChange={onChangeFormField('bankAccountNumber')}
                            label={t('south_african_form_bank_account_number')}
                            errors={errors.bank_account_number}
                            required
                        />
                        <FormField
                            name="bank_branch_code"
                            value={formData?.bankBranchCode}
                            onChange={onChangeFormField('bankBranchCode')}
                            label={t('south_african_form_bank_branch_code')}
                            errors={errors.bank_branch_code}
                            required
                        />
                        <FormField
                            name="address"
                            value={formData?.address}
                            onChange={onChangeFormField('address')}
                            label={t('south_african_form_address')}
                            errors={errors.address}
                            required
                        />
                        <FormField
                            name="country"
                            value={formData?.country}
                            onChange={onChangeFormField('country')}
                            label={t('south_african_form_country')}
                            errors={errors.country}
                            required
                        />
                        <FormField
                            name="phone_number"
                            value={formData?.phoneNumber}
                            onChange={onChangeFormField('phoneNumber')}
                            label={t('south_african_form_phone_number')}
                            errors={errors.phone_number}
                        />

                    </div>
                    {!hidePayButton && (
                        <div className={styles.common_psp_submit}>
                            <Button
                                disabled={hasRequiredFieldNotFilled}
                                label={labelPayButton || t('payButton')}
                                ref={formRef}
                                type='submit'
                            />
                        </div>
                    )}
                </form>
            </div>
        );
    },
);

SouthAfricanForm.propTypes = {
    /**
     * The is of the PSP returned by the backend in order to be able to pass it when we submit the form data.
     */
    pspId: PropTypes.number.isRequired,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
};

SouthAfricanForm.displayName = 'SouthAfricanForm';

export default SouthAfricanForm;
